/**
 * Correspondance entre le `datapointName` de Convera et le champ du formulaire.
 *
 * Jumelle de `webapplication/frontend/src/api/client-beneficiary/pfg-fields.ts`.
 * Les deux applications tiennent la meme table, et c'est un defaut assume : il
 * n'existe pas de paquet partage entre elles aujourd'hui. Un corridor qui
 * gagne un champ doit donc etre corrige des deux cotes — l'extraction est
 * notee dans #70.
 */
export const PFG_FIELD_TO_FORM: Readonly<Record<string, string>> = {
  'beneficiary.businessName': 'companyName',
  'beneficiary.firstName': 'firstName',
  'beneficiary.lastName': 'lastName',
  'beneficiary.address.countryCode': 'addressCountryCode',
  'beneficiary.address.line1': 'addressLine1',
  'beneficiary.address.city': 'addressCity',
  'beneficiary.address.stateOrProv': 'addressState',
  'beneficiary.address.zipOrPostal': 'addressZip',
  'beneficiary.taxId': 'taxId',
  'beneficiary.email': 'email',
  'beneficiary.phoneNumber': 'phoneNumber',
  'beneficiary.dateOfBirth': 'dateOfBirth',
  'bankAccount.accountNumber': 'accountNumber',
  'bankAccount.address.countryCode': 'bankCountryCode',
  'bankAccount.bankCode': 'bankCode',
  'bankAccount.bankName': 'bankName',
  'bankAccount.bankBranchCode': 'bankBranchCode',
  'bankAccount.accountType': 'bankAccountType',
  'bankAccount.bankBranchName': 'bankBranchName',
  'bankAccount.address.line1': 'bankAddressLine1',
  'bankAccount.address.city': 'bankAddressCity',
  'bankAccount.address.stateOrProv': 'bankAddressState',
  'bankAccount.address.zipOrPostal': 'bankAddressZip',
  'payments.currencyCode': 'currencyCode',
};

/** Saisis a l'etape 1, avant meme de connaitre le corridor. */
export const STEP_ONE_FIELDS = [
  'beneficiaryTypeId',
  'companyName',
  'firstName',
  'lastName',
  'currencyCode',
  'bankCountryCode',
  'addressCountryCode',
] as const;

/** Adresse du beneficiaire, montree quel que soit le corridor. */
export const ALWAYS_SHOWN_FIELDS = [
  'addressLine1',
  'addressLine2',
  'addressCity',
  'addressState',
  'addressZip',
] as const;

/**
 * Le vocabulaire de Convera a partir de l'identifiant de type.
 *
 * `beneficiary_type` ne porte pas de colonne code — seulement un libelle et un
 * identifiant fige par le seed. Rend `null` pour un identifiant inconnu :
 * deviner serait pire, un `entityType` errone renvoyant sans erreur la liste
 * de champs d'un autre type de beneficiaire.
 */
export function entityTypeFor(beneficiaryTypeId: number | undefined): string | null {
  if (beneficiaryTypeId === 1) return 'INDIVIDUAL';
  if (beneficiaryTypeId === 2) return 'BUSINESS';
  return null;
}

/** Les champs que le corridor rend obligatoires. Un nom inconnu est ignore. */
export function requiredFormFields(pfgFields: readonly string[]): string[] {
  return [
    ...new Set(
      pfgFields.map((f) => PFG_FIELD_TO_FORM[f]).filter((k): k is string => k !== undefined),
    ),
  ];
}

/** Ce qu'affiche l'etape 2 : les champs exiges, moins ceux deja saisis, plus l'adresse. */
export function stepTwoFields(pfgFields: readonly string[]): string[] {
  const required = requiredFormFields(pfgFields).filter(
    (k) => !(STEP_ONE_FIELDS as readonly string[]).includes(k),
  );
  return [...new Set([...required, ...ALWAYS_SHOWN_FIELDS])];
}

/**
 * Le rail du beneficiaire, dans le vocabulaire du PFG de Convera (#153) :
 * WIRE pour Wire, ACH pour Direct Credit — le reseau domestique du pays de
 * la banque, que Convera resout lui-meme. Absent ou inconnu : WIRE, le seul
 * rail qui existait avant.
 */
export function pfgPaymentMethodFor(railCode: string | null | undefined): 'WIRE' | 'ACH' {
  return railCode === 'DIRECT' ? 'ACH' : 'WIRE';
}
